import { getLocale, translate, translateRich, useLocale } from "../localization";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useExperiences } from "../context/ExperiencesContext";
import ExperiencesApi from "../services/ExperiencesAPI";
import AnalyticsReadAPI, { analyticsResponseToRow, isNewAnalyticsEnabled } from "../services/AnalyticsReadAPI";
import { useAnalytics } from "../context/AnalyticsContext";
import { useAuth } from "../context/AuthContext";
import AnalyticsModeNotice from "../components/dashboard/AnalyticsModeNotice";
import ScoreVisualizations from "../components/answers/ScoreVisualizations";
import Score from "../components/dashboard/Score";
import { AppButton, Panel } from "../components/ui";
import "./QuestionAnswersPage.scss";

function safeDate(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function formatDate(d) {
  if (!d) return "—";
  return d.toLocaleDateString(getLocale(), {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function formatTime(d) {
  if (!d) return "";
  return d.toLocaleTimeString(getLocale(), {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function splitTopics(t) {
  if (!t) return [];
  if (Array.isArray(t)) return t.map(String).filter(Boolean);
  return String(t)
    .split(/[,|]/g)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function QuestionAnswersPage() {
  const locale = useLocale();
  const [params] = useSearchParams();
  const insightId = params.get("insightId") || "";
  const experienceId = params.get("experienceId") || "";
  const q = params.get("q") || "";

  const { experiences } = useExperiences();
  const { dateRange } = useAnalytics();
  const { organizationId } = useAuth();
  const responseScope = useMemo(() => isNewAnalyticsEnabled ? {
    organizationId, startDate: dateRange?.startDate, endDate: dateRange?.endDate,
  } : null, [organizationId, dateRange?.startDate, dateRange?.endDate, locale]);
  const legacyExperiences = isNewAnalyticsEnabled ? null : experiences;

  const [hideBlank, setHideBlank] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedBin, setSelectedBin] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState(null);
  const BIN_SIZE = 20;

  // Scroll to top when component mounts or question changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [insightId, q]);

  const toggleExpanded = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const getAnswerText = (r) =>
    (r.answer || r.transcript || r.studentAnswer || "").toString().trim();

  const getScoreNumber = (r) => {
    if (!getAnswerText(r)) return 0;
    if (isNewAnalyticsEnabled && r.score == null) return null;
    const n = Number(r.score);
    return Number.isFinite(n) ? n : null;
  };

  useEffect(() => {
    let cancelled = false;

    const fetchRows = async () => {
      if (!insightId) {
        setRows([]);
        setRowsLoading(false);
        return;
      }

      if (isNewAnalyticsEnabled) {
        setRowsError(null);
        setRows([]);
        if (!responseScope?.organizationId || !responseScope?.startDate || !responseScope?.endDate) {
          setRowsLoading(false);
          return;
        }
        setRowsLoading(true);
        try {
          const query = new URLSearchParams({ organization: responseScope.organizationId, start: responseScope.startDate, end: responseScope.endDate });
          if (experienceId) query.set("experienceId", experienceId);
          const response = await AnalyticsReadAPI.get(`questions/${encodeURIComponent(insightId)}/responses?${query}`, { requiresAuth: true });
          if (!cancelled) setRows(response.data.map(analyticsResponseToRow).sort((a, b) => String(b.timestamp).localeCompare(String(a.timestamp))));
        } catch (error) {
          if (!cancelled) setRowsError(error);
        } finally {
          if (!cancelled) setRowsLoading(false);
        }
        return;
      }

      const experienceIds = experienceId
        ? [experienceId]
        : Array.isArray(legacyExperiences)
          ? legacyExperiences.map((exp) => exp?.experienceId).filter(Boolean)
          : null;

      if (!experienceIds) {
        return;
      }

      if (experienceIds.length === 0) {
        setRows([]);
        setRowsLoading(false);
        return;
      }

      setRowsLoading(true);

      try {
        const allAssessments = [];
        const BATCH_SIZE = 10;

        for (let i = 0; i < experienceIds.length; i += BATCH_SIZE) {
          const batch = experienceIds.slice(i, i + BATCH_SIZE);

          const batchResults = await Promise.all(
            batch.map(async (expId) => {
              try {
                const query = new URLSearchParams({ experienceId: expId });
                if (insightId) {
                  query.set("insightId", insightId);
                }

                const res = await ExperiencesApi.get(`assessments?${query.toString()}`, {
                  requiresAuth: true,
                });
                const data = res?.data;

                if (!Array.isArray(data)) return [];

                return data.map((assessment) => ({
                  ...assessment,
                  experienceId: assessment?.experienceId ?? expId,
                }));
              } catch {
                return [];
              }
            })
          );

          batchResults.forEach((batchItems) => {
            if (Array.isArray(batchItems)) {
              allAssessments.push(...batchItems);
            }
          });
        }

        if (cancelled) return;

        const nextRows = allAssessments
          .map((assessment) => ({
            timestamp: assessment.lastModified || assessment.createdAt || "",
            score: Number(assessment.score ?? 0),
            answer: (assessment.studentAnswer ?? assessment.transcript ?? "").toString(),
            transcript: (assessment.studentAnswer ?? assessment.transcript ?? "").toString(),
            summary: assessment.summary ?? "",
            feedback: assessment.aiResponse ?? assessment.student_evaluation ?? "",
            topics: assessment.topics ?? "",
            environmentId: assessment.environmentId ?? null,
            sessionCode: assessment.sessionCode ?? assessment.assessmentId ?? null,
            experienceId: assessment.experienceId ?? null,
            insightId: assessment.insightId ?? null,
          }))
          .sort((a, b) => String(b.timestamp).localeCompare(String(a.timestamp)));

        setRows(nextRows);
      } catch {
        if (!cancelled) {
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setRowsLoading(false);
        }
      }
    };

    fetchRows();

    return () => {
      cancelled = true;
    };
  }, [experienceId, legacyExperiences, insightId, responseScope]);

  const loading = rowsLoading || (!isNewAnalyticsEnabled && experiences === null);

  const avgScore = useMemo(() => {
    if (!rows.length) return null;
    const scored = rows.map((r) => getScoreNumber(r)).filter((n) => typeof n === "number");
    if (!scored.length) return null;
    return Math.round(scored.reduce((a, b) => a + b, 0) / scored.length);
  }, [rows, locale]);

  const blankCount = useMemo(() => rows.filter((r) => !getAnswerText(r)).length, [rows, locale]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const ans = getAnswerText(r);
      const score = getScoreNumber(r);

      if (hideBlank && !ans) return false;
      if (isNewAnalyticsEnabled && selectedBin && score == null) return false;
      if (selectedBin && score != null) {
        const inBin = score >= selectedBin.min && (score < selectedBin.max || (selectedBin.max === 100 && score === 100));
        if (!inBin) return false;
      }
      if (search && !ans.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [rows, hideBlank, search, selectedBin, locale]);

  const resultsLabel = loading
    ? translate("Loading responses\u2026")
    : filtered.length !== rows.length
      ? translate("{shown} shown of {total}", { shown: filtered.length.toLocaleString(getLocale()), total: rows.length.toLocaleString(getLocale()) })
      : translate("{shown} shown", { shown: filtered.length.toLocaleString(getLocale()) });

  return (
    <div className="qaPage">
      <div className="qaContainer">
        <AnalyticsModeNotice error={rowsError} />
        <Link className="qaBackLink" to="/dashboard">{translate("\u2190 Back to Dashboard")}</Link>

        <div className="qaTopGrid">
          <Panel className="qaHeader" row={false}>
            <div className="qaHeaderMeta">{translate("Insight Question")}</div>
            <div className="qaHeaderId">{translateRich("Insight ID {value0}", {
  value0: insightId || "—"
})}</div>
            <h1 className="qaHeaderQuestion">{q || translate("(no question provided)")}</h1>
          </Panel>

          <Panel className="qaSection qaSection--viz" row={false}>
            <div className="qaSectionHeader">
              <div>
                <div className="qaSectionEyebrow">{translate("Overview")}</div>
                <div className="qaSectionTitle">{translate("Response distribution")}</div>
              </div>
            </div>
            <div className="qaSectionBody">
              <ScoreVisualizations
                rows={rows}
                getAnswerText={getAnswerText}
                getScoreNumber={getScoreNumber}
                selectedBin={selectedBin}
                setSelectedBin={setSelectedBin}
                binSize={BIN_SIZE}
                avgScore={avgScore}
                preserveUnscored={isNewAnalyticsEnabled}
                blankCount={blankCount}
              />
            </div>
          </Panel>
        </div>

        <Panel className="qaSection qaSection--responses" row={false}>
          <div className="qaSectionHeader qaSectionHeader--responses">
            <div>
              <div className="qaSectionEyebrow">{translate("Responses")}</div>
              <div className="qaSectionTitle">{translate("Student responses")}</div>
              <div className="qaSectionSubtitle">{resultsLabel}</div>
            </div>
          </div>
          <div className="qaToolbar">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="qaSearch"
              placeholder={translate("Search responses")}
            />
            <AppButton
              className="qaPill qaBlankToggle"
              onClick={() => setHideBlank((v) => !v)}
              type="button"
              kind="secondary"
              aria-pressed={hideBlank}
            >
              {hideBlank ? translate("Show blank responses") : translate("Hide blank responses")}
            </AppButton>
          </div>

          {loading ? (
            <div className="qaEmpty">{translate("Loading answers\u2026")}</div>
          ) : rows.length === 0 ? (
            <div className="qaEmpty">{translate("No responses found for this question.")}</div>
          ) : (
            <div className="qaTable app-row-list">
              <div className="qaTableHeader">
                <div className="qaTh">{translate("DATE")}</div>
                <div className="qaTh">{translate("NAME")}</div>
                <div className="qaTh">{translate("SCORE")}</div>
                <div className="qaTh">{translate("ANSWER")}</div>
                <div className="qaTh qaThAction" />
              </div>

              {filtered.map((r, idx) => {
                const key = `${r.timestamp || ""}-${r.sessionCode || ""}-${idx}`;
                const isOpen = expanded.has(key);
                const answer = getAnswerText(r);
                const isBlank = !answer;
                const d = safeDate(r.timestamp);
                const topics = splitTopics(r.topics);
                const hasDetails = Boolean(r.summary || r.feedback || topics.length);

                return (
                  <Panel key={key} className="qaTr" hoverable>
                    <div className="qaTd qaTdDate">
                      <div className="qaDateMain">{formatDate(d)}</div>
                      <div className="qaDateSub">{formatTime(d)}</div>
                    </div>
                    <div className="qaTd qaTdName">
                      <div className="qaNameSub">{translate("Anonymous")}</div>
                      <div className="qaNameMain">{r.sessionCode ? r.sessionCode.slice(0, 4) : "—"}</div>
                    </div>
                    <div className="qaTd qaTdScore">
                      <Score score={getScoreNumber(r)} />
                    </div>
                    <div className="qaTd qaTdAnswer">
                      <div className={`qaAnswer ${isBlank ? "isBlank" : ""}`}>
                        {answer || translate("No response provided")}
                      </div>
                      {hasDetails && isOpen && (
                        <div className="qaDetails">
                          {r.summary && (
                            <div className="qaDetailBlock">
                              <div className="qaDetailLabel">{translate("SUMMARY")}</div>
                              <div className="qaDetailText">{r.summary}</div>
                            </div>
                          )}
                          {r.feedback && (
                            <div className="qaDetailBlock">
                              <div className="qaDetailLabel">{translate("AI FEEDBACK")}</div>
                              <div className="qaDetailText">{r.feedback}</div>
                            </div>
                          )}
                          {topics.length > 0 && (
                            <div className="qaDetailBlock">
                              <div className="qaDetailLabel">{translate("TOPICS")}</div>
                              <div className="qaTopics">
                                {topics.map((t) => (
                                  <span key={t} className="qaTopic">{t}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="qaTd qaTdAction">
                      {hasDetails ? (
                        <AppButton
                          className={`qaPill ${isOpen ? "isActive" : ""}`}
                          type="button"
                          kind="secondary"
                          onClick={() => toggleExpanded(key)}
                        >
                          {isOpen ? "HIDE" : "VIEW"}
                        </AppButton>
                      ) : (
                        <div className="qaNoAction">—</div>
                      )}
                    </div>
                  </Panel>
                );
              })}

              {filtered.length === 0 && (
                <div className="qaEmpty">{translate("No results match your filters.")}</div>
              )}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
